import { http, download } from './request'
import type { RequestConfig } from './types'

export type PermissionApplyStatus = 'pending' | 'approved' | 'rejected'

export interface PermissionApplicationStats {
  pendingApplicationCount: number
  authorizedUserCount: number
}

export interface PermissionAttachmentRef {
  fileId?: string
  fileName: string
}

export interface PermissionApplicationRecord {
  id: string
  applicant: string
  permissionScope: string
  region: string
  /** 行政区划码（详情接口可能返回） */
  addressCode?: string
  /** 目标组织名称（详情接口） */
  targetOrgName?: string
  /** 详情：申请人当前部门/组织（审批前对比） */
  currentOrgName?: string
  /** 详情：当前所属地区展示名 */
  currentRegion?: string
  /** 详情：当前组织关联区划码 */
  currentAddressCode?: string
  /** 详情：当前拥有角色（顿号分隔） */
  currentRoles?: string
  /** 申请说明 */
  note?: string
  createdAt: string
  attachmentCount: number
  status: PermissionApplyStatus
  rejectReason?: string
  attachments?: PermissionAttachmentRef[]
}

export interface PermissionPageParams {
  page: number
  pageSize: number
}

export interface PermissionPageResult {
  list: PermissionApplicationRecord[]
  total: number
}

export interface CreatePermissionApplicationPayload {
  /** 兼容旧前端；服务端以当前登录用户为准 */
  applicantUserId?: string
  applicant?: string
  /** 角色名（兼容旧后端/前端回显） */
  permissionScope?: string
  /** 角色ID（兼容旧前端；多选时取首项） */
  roleId?: string
  /** 申请切换到的目标组织 */
  targetOrgId: string
  /** 申请的角色（多选） */
  roleIds: string[]
  /** 组织关联的 9 位行政区划码 */
  addressCode?: string
  /** 所属地区名称（展示用） */
  region?: string
  note?: string
  attachmentCount?: number
  attachmentNames?: string[]
  attachmentIds?: string[]
}

export async function fetchPermissionStats(): Promise<PermissionApplicationStats> {
  return await http.get<PermissionApplicationStats>('/biz/overseas/permissions/stats')
}

export async function fetchPermissionApplyPage(
  params: PermissionPageParams,
): Promise<PermissionPageResult> {
  return await http.get<PermissionPageResult>('/biz/overseas/permissions/apply/page', { params })
}

export async function fetchPermissionApprovalPage(
  params: PermissionPageParams,
): Promise<PermissionPageResult> {
  return await http.get<PermissionPageResult>('/biz/overseas/permissions/approval/page', { params })
}

export async function fetchPermissionListPage(
  params: PermissionPageParams,
): Promise<PermissionPageResult> {
  return await http.get<PermissionPageResult>('/biz/overseas/permissions/list/page', { params })
}

export async function fetchPermissionApplicationDetail(
  id: string,
): Promise<PermissionApplicationRecord | null> {
  return await http.get<PermissionApplicationRecord>(`/biz/overseas/permissions/${id}`)
}

export async function createPermissionApplication(
  payload: CreatePermissionApplicationPayload,
): Promise<void> {
  await http.post<void>('/biz/overseas/permissions/apply', payload)
}

export async function downloadPermissionApplicationAttachments(id: string): Promise<void> {
  await download(`/biz/overseas/permissions/${id}/attachments/download`)
}

export async function fetchRegionNameByAddressCode(
  addressCode: string,
  req?: Pick<RequestConfig, 'skipErrorMessage'>,
): Promise<string> {
  const code = addressCode?.trim()
  if (!code) return ''
  return await http.get<string>('/biz/overseas/permissions/regionName', {
    params: { addressCode: code },
    skipErrorMessage: req?.skipErrorMessage,
  })
}

export async function approvePermissionApplication(id: string): Promise<void> {
  const safe = encodeURIComponent(id)
  // 显式 JSON 体，避免部分网关/代理对无 body POST 处理异常
  await http.post<void>(`/biz/overseas/permissions/${safe}/approve`, {})
}

export async function rejectPermissionApplication(id: string, reason: string): Promise<void> {
  const safe = encodeURIComponent(id)
  await http.post<void>(`/biz/overseas/permissions/${safe}/reject`, { reason })
}

export async function deletePermissionApplication(id: string): Promise<void> {
  const safe = encodeURIComponent(id)
  await http.post<void>(`/biz/overseas/permissions/${safe}/delete`, {})
}
